import { Component, OnInit, inject, ViewChild } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NewsService } from '../../../shared/services/news/news.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import {
  provideNativeDateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { StorageService } from '../../../shared/services/storage/storage.service';

// Define custom date formats
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-edit-detail-incorporate',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    FormsModule,
    MatTimepickerModule,
    UploadFilesComponent,
    RouterLink,
    QuillModule,
    LoaderComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './edit-detail-incorporate.component.html',
  styleUrls: [
    './edit-detail-incorporate.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class EditDetailIncorporateComponent implements OnInit {
  value: Date | undefined;
  modules: any;
  myroles: any = {};

  addition: any = [];
  toppingList: string[] = ['Incorporación', 'Desvinculación', 'Cambios'];
  id: number = 0;

  // Form group
  incorporateForm: FormGroup;

  // For date and time
  isProgrammed: boolean = false;
  dateValue: Date | null = null;
  timeValue: string = '00:00';

  // Add these properties for image handling
  selectedImage: File | null = null;
  imageRemoved: boolean = false;

  @ViewChild(UploadFilesComponent) uploadFilesComponent!: UploadFilesComponent;

  private _snackBar = inject(MatSnackBar);
  // Función para mostrar un snackbar
  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  constructor(
    private route: ActivatedRoute,
    private newsService: NewsService,
    private router: Router,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private rolesService: RolesService,
    private storageService: StorageService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();

    this.incorporateForm = this.fb.group({
      title: ['', [Validators.required]],
      observation: ['', [Validators.required]],
      toppings: ['', [Validators.required]],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  getFormattedDateTime(): string {
    if (!this.dateValue) return 'Fecha no seleccionada';

    return `${this.dateValue.toLocaleDateString()} ${this.timeValue}`;
  }

  // When you need the combined datetime value (e.g., for saving)
  getCombinedDateTime(): Date | null {
    if (!this.dateValue) return null;
    const base = this.dateValue;
    const [hh, mm] = this.timeValue.split(':').map(Number);
    const local = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      isNaN(hh) ? 0 : hh,
      isNaN(mm) ? 0 : mm,
      0,
      0
    );
    return local; // toISOString() elsewhere will yield true UTC
  }

  // Add methods for handling images
  onFileSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedImage = files[0];
      // console.log('Image selected:', this.selectedImage);
    }
  }

  removeImage(): void {
    this.addition.image_url = null;
    this.imageRemoved = true;
  }

  saveChanges() {
    this.loaderService.showLoader();
    if (this.incorporateForm.invalid) {
      this.loaderService.hideLoader();
      this.incorporateForm.markAllAsTouched();
      this.openSnackBar('Por favor, complete todos los campos obligatorios.');
      return;
    }

    const formValues = this.incorporateForm.value;

    // Create FormData object for the multipart/form-data request
    const formData = new FormData();

    // Get content with special handling for emojis
    let content = formValues.observation;

    // Try to get the actual HTML content from the Quill editor to preserve emojis
    try {
      const quillEditor = document.querySelector('.ql-editor') as HTMLElement;
      if (quillEditor && quillEditor.innerHTML) {
        const htmlContent = quillEditor.innerHTML;

        // Always use the HTML content from the editor as it preserves emojis better
        //console.log('Using HTML content from Quill editor to preserve emojis');
        content = htmlContent;
      }
    } catch (error) {
      // console.log('Could not extract HTML content, using form value');
    }

    formData.append('title', formValues.title);
    if (content) {
      formData.append('content', content);
      // console.log('Updating incorporation with content:', content);
    }
    formData.append('addition_type', formValues.toppings);
    formData.append('programmed', this.isProgrammed.toString());

    // Fix: Use the combined date-time that preserves the exact time specified
    let publicationDate: string;
    if (this.isProgrammed) {
      const combinedDateTime = this.getCombinedDateTime();
      publicationDate = combinedDateTime
        ? combinedDateTime.toISOString()
        : new Date().toISOString();

      // console.log(
      //   `Saving publication date: ${publicationDate}, original time input: ${this.timeValue}`
      // );
    } else {
      publicationDate = new Date().toISOString();
    }
    formData.append('publication_date', publicationDate);

    // Preserve notifications setting
    formData.append('notifications', this.addition.notifications.toString());

    // Add image if a new one is selected
    if (this.selectedImage) {
      formData.append('image', this.selectedImage);
    }

    // If image was removed, send flag
    if (this.imageRemoved) {
      formData.append('remove_image', 'true');
      // console.log('Image removal flag set to true');
    }

    // console.log('FormData being sent:', formData);

    this.newsService.putTeamChanges(this.id, formData).subscribe({
      next: (response: any) => {
        this.loaderService.hideLoader();
        this.openSnackBar('Incorporación actualizada con éxito.');
        this.router.navigate(['/home/communication/incorporation']);
      },
      error: (error: any) => {
        this.loaderService.hideLoader();
        console.error(error);
        this.openSnackBar('Error al actualizar la incorporación.');
      },
    });
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'incorporations',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'incorporations',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/communication/incorporation',
        'No tienes permisos para editar una incorporación'
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  ngOnInit() {
    this.loaderService.showLoader();
    this.route.params.subscribe((params) => {
      this.id = params['id'];
    });

    this.newsService.getTeamChangesId(this.id).subscribe({
      next: (addition) => {
        this.loaderService.hideLoader();
        // console.log('Addition data:', addition);
        this.addition = addition;

        this.incorporateForm.patchValue({
          title: addition.title,
          observation: addition.content,
          toppings: addition.addition_type,
        });

        this.isProgrammed = addition.programmed;

        // Extract time from publication_date using UTC methods to avoid timezone conversion
        if (addition.publication_date) {
          const dateObj = new Date(addition.publication_date);
          if (!isNaN(dateObj.getTime())) {
            // Use getUTC methods to get the time exactly as stored in the server
            const hours = dateObj.getUTCHours().toString().padStart(2, '0');
            const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
            this.timeValue = `${hours}:${minutes}`;

            // For display purposes, set the date
            this.dateValue = new Date(
              dateObj.getUTCFullYear(),
              dateObj.getUTCMonth(),
              dateObj.getUTCDate()
            );

            // console.log(
            //   `Extracted time from server: ${hours}:${minutes}, date: ${this.dateValue.toDateString()}`
            // );
          }
        }
      },
    });

    this.loadRoles();
  }
}
